/**
 * Pure risk vocabulary and deterministic classification module (RISKFMT01).
 *
 * Implements Phalcon compliance study item 2 (Section 4, rank 2):
 * - Indicator categories as pure data with id, label, sources, lens rules, and one-line meaning.
 * - Named risk levels as an ordered list with the rule setting each:
 *   Listed > Reported > Indicated > No hit / Not tested.
 * - Deterministic classification from internal platform rows only (R10 honest uncertainty).
 * - TL-904: A vendor score (MetaSleuth risk_advisory) is NEVER an input.
 */

export interface IndicatorCategoryDef {
  id: string;
  label: string;
  sources: string[];
  rules: string[];
  meaning: string;
}

export type RiskLevel = 'Listed' | 'Reported' | 'Indicated' | 'No hit' | 'Not tested';

export interface RiskLevelDef {
  level: RiskLevel;
  rule: string;
  meaning: string;
}

/**
 * The 13 indicator categories as data (RISKFMT01 step 1).
 */
export const INDICATOR_CATEGORIES: readonly IndicatorCategoryDef[] = [
  {
    id: 'sanctions_list',
    label: 'sanctions list',
    sources: ['sanctions'],
    rules: ['sanctions_list', 'sanctions_terror_program'],
    meaning: 'Entry on an international sanctions list held by the platform (OFAC, OpenSanctions wallets).',
  },
  {
    id: 'issuer_blacklist',
    label: 'issuer blacklist',
    sources: ['issuer_freeze', 'tron', 'trongrid'],
    rules: ['issuer_blacklist'],
    meaning: 'Blacklisted and frozen on-chain by the token issuer (e.g. Tether or Circle contract blacklist).',
  },
  {
    id: 'major_crime_label',
    label: 'major-crime label',
    sources: ['entity_db'],
    rules: ['major_category', 'terror_label', 'drugs_arms_label', 'hawala_label'],
    meaning: 'Entity database category or label keyword for darknet, terrorism, arms, drugs, or hawala.',
  },
  {
    id: 'scam_fraud_label',
    label: 'scam or fraud label',
    sources: ['entity_db'],
    rules: ['cyber_category'],
    meaning: 'Entity database record identifying scam, fraud, phishing, theft, or ransomware.',
  },
  {
    id: 'community_report',
    label: 'community report',
    sources: ['chainabuse', 'case_cti'],
    rules: ['reported_association', 'report_claim'],
    meaning: 'Community abuse report (ChainAbuse) or case claim alleging illicit activity (report, not proof).',
  },
  {
    id: 'threat_intel_advisory',
    label: 'threat-intel advisory',
    sources: ['case_cti'],
    rules: ['advisory_domain', 'sanction_note'],
    meaning: 'Threat-intelligence advisory, CERT-In advisory domain, or sanctions note attached to the case.',
  },
  {
    id: 'mixer_exposure',
    label: 'mixer exposure',
    sources: ['entity_db'],
    rules: ['mixer_at_scale'],
    meaning: 'Mixer or privacy pool touched by multiple transfers in the case graph.',
  },
  {
    id: 'rapid_sweep_sink',
    label: 'rapid sweep into a sink',
    sources: ['engine', 'facts'],
    rules: ['rapid_sweep', 'rapid_sweep_sink', 'sweep_incident'],
    meaning: 'Rapid multi-hop sweeping of funds directly into an illicit or unhosted sink.',
  },
  {
    id: 'exchange_deposit',
    label: 'exchange deposit (identity route)',
    sources: ['entity_db'],
    rules: ['kyc_exchange_deposit', 'kyc_card_gateway'],
    meaning: 'Custodial exchange deposit or card gateway address where notice yields customer identity.',
  },
  {
    id: 'p2p_desk',
    label: 'P2P desk',
    sources: ['p2p'],
    rules: ['p2p_desk_scale', 'p2p_ad_risk'],
    meaning: 'Over-the-counter or peer-to-peer merchant desk operating at volume or high-risk score.',
  },
  {
    id: 'operator_cluster',
    label: 'operator cluster of another case',
    sources: ['operator_clusters'],
    rules: ['operator_cluster_members', 'operator_cluster_volume'],
    meaning: 'Clustered co-operator wallet linked to another case visible to the investigator.',
  },
  {
    id: 'harvested_identifier',
    label: 'harvested identifier',
    sources: ['mulehunter'],
    rules: ['mule_network_scale', 'mule_identifier'],
    meaning: 'Identifier harvested from mule recruitment networks or fraud channels.',
  },
  {
    id: 'funds_held',
    label: 'funds held (live balance above zero)',
    sources: ['live_balance', 'tron', 'trongrid'],
    rules: ['funds_held', 'holds_balance'],
    meaning: 'On-chain query confirmed liquid tokens or gas balance currently unspent at this address.',
  },
] as const;

/** Category lookup by id */
export const CATEGORY_BY_ID = new Map<string, IndicatorCategoryDef>(
  INDICATOR_CATEGORIES.map((c) => [c.id, c])
);

/**
 * Standard 8 sources covering wallets in order.
 */
export const APPLICABLE_WALLET_SOURCES = [
  'sanctions',
  'entity_db',
  'chainabuse',
  'case_cti',
  'p2p',
  'mulehunter',
  'operator_clusters',
  'watchlist',
] as const;

/**
 * The 5 risk levels as an ordered list with the rule that sets each (RISKFMT01 step 1).
 */
export const RISK_LEVELS: readonly RiskLevelDef[] = [
  {
    level: 'Listed',
    rule: 'a sanctions or issuer-blacklist hit, or a listed major-crime hit',
    meaning: 'A sanctions list hit, token issuer freeze/blacklist, or listed major-crime database record.',
  },
  {
    level: 'Reported',
    rule: 'a report or label hit of major-crime or cybercrime relevance',
    meaning: 'A community abuse report, case claim, or free-text label pointing to major crime or cybercrime.',
  },
  {
    level: 'Indicated',
    rule: 'only indicator hits, or sweep or matrix-role indicators',
    meaning: 'Inferred from scale thresholds (mixers, high-volume P2P, clusters), rapid sweep, or seizure-matrix nexus role.',
  },
  {
    level: 'No hit',
    rule: 'every applicable source clear',
    meaning: 'Every applicable internal source was consulted and found no record for this identifier.',
  },
  {
    level: 'Not tested',
    rule: 'no hit and at least one applicable source not_tested',
    meaning: 'No hit found, but one or more applicable internal sources could not be consulted or failed.',
  },
] as const;

export interface ClassifiedCategory {
  id: string;
  label: string;
  sources: string[];
  rule: string;
}

export interface RiskClassification {
  level: RiskLevel;
  categories: ClassifiedCategory[];
  untested_sources: string[];
  suppressed_count?: number;
}

export interface RiskClassificationOptions {
  sources?: Record<string, unknown> | null;
  untested_sources?: string[];
  allClear?: boolean;
}

/**
 * Returns a short "How levels are set" paragraph generated directly from the module's table.
 */
export function howLevelsAreSetText(): string {
  const parts = RISK_LEVELS.map((l) => `${l.level} (${l.rule})`);
  return `How levels are set: ${parts.join('; ')}. Vendor risk scores are never an input (TL-904); levels derive only from verified internal database rows, on-chain states, and stated rules.`;
}

/**
 * Returns the "How levels are set" paragraph as HTML.
 */
export function howLevelsAreSetHtml(): string {
  const listItems = RISK_LEVELS.map(
    (l) => `<li><strong>${l.level}</strong>: ${l.rule}.</li>`
  ).join('');
  return `<p class="muted" data-testid="how-levels-are-set"><strong>How levels are set:</strong> `
    + `A screening result is classified strictly from internal platform database records and on-chain facts. `
    + `Vendor scores (such as MetaSleuth risk advisories) are never an input (TL-904).</p>`
    + `<ul class="muted text-xs" data-testid="how-levels-are-set-list">${listItems}</ul>`;
}

/** Formats category entries with their sources in one line: e.g. "sanctions list (sanctions), issuer blacklist (issuer_freeze)" */
export function formatCategoriesLine(categories: ClassifiedCategory[]): string {
  if (!categories || categories.length === 0) return '';
  return categories
    .map((c) => `${c.label}${c.sources.length ? ` (${c.sources.join(', ')})` : ''}`)
    .join(' · ');
}

type RawRow = Record<string, unknown>;

function getSourceStatus(val: unknown): string {
  if (typeof val === 'string') return val.trim().toLowerCase();
  if (val && typeof val === 'object' && 'status' in val) {
    return String((val as { status: unknown }).status || '').trim().toLowerCase();
  }
  return '';
}

function isOutOfScopeStatus(st: string): boolean {
  return st === 'n/a' || st === 'not_applicable';
}

function isUntestedStatus(st: string): boolean {
  return st === 'not_tested' || st === 'unconsulted';
}

function isConsultedStatus(st: string): boolean {
  return st === 'clear' || st === 'ok' || st === 'consulted' || st === 'hit';
}

/**
 * Classifies an identifier, target, or set of screening rows into a named RiskLevel,
 * its indicator categories, and any untested sources.
 *
 * Implements TL-904: Vendor score (MetaSleuth risk_advisory) is NEVER an input.
 */
export function classify(rows?: unknown, options?: RiskClassificationOptions): RiskClassification {
  const categoryMap = new Map<string, { sources: Set<string>; rule: string; label: string }>();
  const untestedSet = new Set<string>();
  const consultedSet = new Set<string>();
  const applicableSources = new Set<string>();
  let hasListed = false;
  let hasReported = false;
  let hasIndicated = false;
  let suppressedCount = 0;

  function processSourceEntry(sKey: string, sVal: unknown) {
    const st = getSourceStatus(sVal);
    if (isOutOfScopeStatus(st)) {
      // Out of scope: not required for No hit and not added to untestedSources
      return;
    }
    applicableSources.add(sKey);
    if (isUntestedStatus(st)) {
      untestedSet.add(sKey);
    } else if (isConsultedStatus(st)) {
      consultedSet.add(sKey);
    }
  }

  // 1. Process options sources
  if (options?.untested_sources) {
    for (const s of options.untested_sources) {
      untestedSet.add(s);
      applicableSources.add(s);
    }
  }
  if (options?.sources && typeof options.sources === 'object') {
    for (const [sKey, sVal] of Object.entries(options.sources)) {
      processSourceEntry(sKey, sVal);
    }
  }

  // 2. Unpack rows
  let rawList: RawRow[] = [];
  if (Array.isArray(rows)) {
    rawList = rows.filter((r): r is RawRow => Boolean(r && typeof r === 'object'));
  } else if (rows && typeof rows === 'object') {
    const obj = rows as RawRow;
    // Check if it's a ScreeningIdentifier with hits
    if (Array.isArray(obj.hits)) {
      rawList = obj.hits.filter((r): r is RawRow => Boolean(r && typeof r === 'object'));
      if (Array.isArray(obj.untested_sources)) {
        for (const s of obj.untested_sources) {
          untestedSet.add(String(s));
          applicableSources.add(String(s));
        }
      }
    } else if (Array.isArray(obj.rows)) {
      rawList = obj.rows.filter((r): r is RawRow => Boolean(r && typeof r === 'object'));
    } else {
      rawList = [obj];
    }
    // Also check if sources object is nested
    if (obj.sources && typeof obj.sources === 'object') {
      for (const [sKey, sVal] of Object.entries(obj.sources as Record<string, unknown>)) {
        processSourceEntry(sKey, sVal);
      }
    }
    if (Array.isArray(obj.untested_sources)) {
      for (const s of obj.untested_sources) {
        untestedSet.add(String(s));
        applicableSources.add(String(s));
      }
    }
  }

  function addCategory(catId: string, source: string, rule: string) {
    const def = CATEGORY_BY_ID.get(catId);
    const label = def?.label || catId.replace(/_/g, ' ');
    const existing = categoryMap.get(catId);
    if (!existing) {
      categoryMap.set(catId, {
        sources: new Set([source]),
        rule,
        label,
      });
    } else {
      if (source) existing.sources.add(source);
    }
  }

  // 3. Evaluate each row
  for (const row of rawList) {
    // TL-904: Vendor risk advisory is NEVER an evidence input
    // If this row is solely a vendor score or only holds risk_advisory, ignore it.
    if (
      row.source === 'MetaSleuth' ||
      row.source === 'vendor' ||
      row.source === 'vendor_metasleuth' ||
      row.type === 'vendor_score' ||
      (row.score !== undefined && row.retrieved_on !== undefined && !row.relevance && !row.rule)
    ) {
      continue;
    }

    // Check for suppression (ALLOW01 support)
    if (row.suppressed === true || (row.suppressed && typeof row.suppressed === 'object')) {
      suppressedCount++;
      continue;
    }

    // Check status rows
    const rowStatus = typeof row.status === 'string' ? row.status.toLowerCase() : '';
    if (isOutOfScopeStatus(rowStatus)) {
      continue;
    }
    if (isUntestedStatus(rowStatus) && row.source) {
      untestedSet.add(String(row.source));
      applicableSources.add(String(row.source));
      continue;
    } else if (isConsultedStatus(rowStatus) && row.source) {
      consultedSet.add(String(row.source));
      applicableSources.add(String(row.source));
      continue;
    }

    const rule = String(row.rule || '');
    const kind = String(row.kind || '');
    const source = String(row.source || '');
    const relevance = String(row.relevance || '');
    const strength = String(row.strength || '');
    const category = String(row.category || '');
    const blacklisted = row.blacklisted === true;
    const isFrozen = Boolean(
      (row.deep_dive as RawRow)?.freeze_status &&
      ((row.deep_dive as RawRow).freeze_status as RawRow).is_frozen === true
    );
    const liveBal = typeof row.live_balance === 'number'
      ? row.live_balance
      : typeof row.live_trx_balance === 'number'
        ? row.live_trx_balance
        : undefined;
    const liveCheck = typeof row.live_check === 'string' ? row.live_check : '';
    const liveRule = String(row.live_rule || '');
    const isRapidSweep = Boolean(
      row.is_rapid_sweep === true ||
      row.rapid_sweep === true ||
      row.incident === 'rapid_sweep' ||
      rule === 'rapid_sweep' ||
      rule === 'rapid_sweep_sink'
    );
    const nexusRole = typeof row.nexus_role === 'string' ? row.nexus_role.toLowerCase() : '';

    // Category 1: sanctions_list (Level: Listed)
    if (
      rule === 'sanctions_list' ||
      rule === 'sanctions_terror_program' ||
      source === 'sanctions' ||
      kind === 'sanctions'
    ) {
      addCategory('sanctions_list', source || 'sanctions', rule || 'sanctions_list');
      hasListed = true;
    }

    // Category 2: issuer_blacklist (Level: Listed)
    if (
      blacklisted ||
      isFrozen ||
      liveRule.includes('already frozen') ||
      liveRule === 'blacklisted' ||
      category === 'issuer_blacklist' ||
      rule === 'issuer_blacklist'
    ) {
      addCategory('issuer_blacklist', String(row.live_source || 'issuer_freeze'), rule || 'issuer_blacklist');
      hasListed = true;
    }

    // Category 3: major_crime_label
    if (
      rule === 'major_category' ||
      (relevance === 'major_crime' && strength === 'listed')
    ) {
      addCategory('major_crime_label', source || 'entity_db', rule || 'major_category');
      hasListed = true;
    } else if (
      rule === 'terror_label' ||
      rule === 'drugs_arms_label' ||
      rule === 'hawala_label' ||
      (relevance === 'major_crime' && strength === 'label')
    ) {
      addCategory('major_crime_label', source || 'entity_db', rule || 'terror_label');
      hasReported = true;
    }

    // Category 4: scam_fraud_label (Level: Reported)
    if (
      rule === 'cyber_category' ||
      (relevance === 'cybercrime' && strength === 'listed' && kind !== 'mule_identifier')
    ) {
      addCategory('scam_fraud_label', source || 'entity_db', rule || 'cyber_category');
      hasReported = true;
    }

    // Category 5: community_report (Level: Reported)
    if (
      rule === 'reported_association' ||
      rule === 'report_claim' ||
      source === 'chainabuse' ||
      kind === 'chainabuse_report' ||
      kind === 'report_claim' ||
      (relevance === 'cybercrime' && strength === 'report')
    ) {
      addCategory('community_report', source || 'chainabuse', rule || 'reported_association');
      hasReported = true;
    }

    // Category 6: threat_intel_advisory (Level: Reported)
    if (
      rule === 'advisory_domain' ||
      rule === 'sanction_note' ||
      kind === 'advisory' ||
      kind === 'scam_domain' ||
      kind === 'sanction_note'
    ) {
      addCategory('threat_intel_advisory', source || 'case_cti', rule || 'advisory_domain');
      hasReported = true;
    }

    // Category 7: mixer_exposure (Level: Indicated)
    if (
      rule === 'mixer_at_scale' ||
      (relevance === 'major_crime' && strength === 'indicator' && (kind === 'mixer' || category.includes('mixer')))
    ) {
      addCategory('mixer_exposure', source || 'entity_db', rule || 'mixer_at_scale');
      hasIndicated = true;
    }

    // Category 8: rapid_sweep_sink (Level: Indicated)
    if (isRapidSweep) {
      addCategory('rapid_sweep_sink', source || 'engine', rule || 'rapid_sweep_sink');
      hasIndicated = true;
    }

    // Category 9: exchange_deposit (Level: Indicated)
    if (
      rule === 'kyc_exchange_deposit' ||
      rule === 'kyc_card_gateway' ||
      relevance === 'kyc_route' ||
      kind === 'card_gateway' ||
      (kind === 'exchange' && (row.facts as RawRow)?.wallet_role === 'deposit') ||
      nexusRole === 'off_ramp' ||
      nexusRole === 'exchange_deposit'
    ) {
      addCategory('exchange_deposit', source || 'entity_db', rule || 'kyc_exchange_deposit');
      hasIndicated = true;
    }

    // Category 10: p2p_desk (Level: Indicated)
    if (
      rule === 'p2p_desk_scale' ||
      rule === 'p2p_ad_risk' ||
      source === 'p2p' ||
      kind === 'p2p_merchant'
    ) {
      addCategory('p2p_desk', source || 'p2p', rule || 'p2p_desk_scale');
      hasIndicated = true;
    }

    // Category 11: operator_cluster (Level: Indicated)
    if (
      rule === 'operator_cluster_members' ||
      rule === 'operator_cluster_volume' ||
      source === 'operator_clusters' ||
      kind === 'operator_cluster'
    ) {
      addCategory('operator_cluster', source || 'operator_clusters', rule || 'operator_cluster_members');
      hasIndicated = true;
    }

    // Category 12: harvested_identifier
    if (
      rule === 'mule_network_scale' ||
      rule === 'mule_identifier' ||
      source === 'mulehunter' ||
      kind === 'mule_identifier'
    ) {
      addCategory('harvested_identifier', source || 'mulehunter', rule || 'mule_identifier');
      if (rule === 'mule_network_scale' || strength === 'indicator') {
        hasIndicated = true;
      } else {
        hasReported = true;
      }
    }

    // Category 13: funds_held (Level: Indicated)
    if (
      (liveBal !== undefined && liveBal > 0) ||
      liveRule === 'holds_balance' ||
      category === 'funds_held' ||
      rule === 'funds_held'
    ) {
      addCategory('funds_held', String(row.live_source || 'live_balance'), rule || 'funds_held');
      hasIndicated = true;
    }

    // Additional matrix-role indicators
    if (
      nexusRole &&
      nexusRole !== 'off_ramp' &&
      nexusRole !== 'exchange_deposit' &&
      !hasListed &&
      !hasReported
    ) {
      hasIndicated = true;
    }

    // Scale / indicator strength fallback
    if (strength === 'indicator') {
      hasIndicated = true;
    }
  }

  // 4. Assemble classified categories
  const categories: ClassifiedCategory[] = Array.from(categoryMap.entries()).map(([id, info]) => ({
    id,
    label: info.label,
    sources: Array.from(info.sources),
    rule: info.rule,
  }));

  const untested_sources = Array.from(untestedSet);

  // 5. Determine level
  let level: RiskLevel;
  if (hasListed) {
    level = 'Listed';
  } else if (hasReported) {
    level = 'Reported';
  } else if (hasIndicated || categories.length > 0) {
    level = 'Indicated';
  } else {
    // No hits found
    if (untested_sources.length > 0) {
      // R10: A source was not tested, never collapse to No hit
      level = 'Not tested';
    } else if (options?.allClear) {
      level = 'No hit';
    } else if (
      applicableSources.size > 0 &&
      Array.from(applicableSources).every((s) => consultedSet.has(s))
    ) {
      // Every applicable source was consulted (clear, ok, consulted, hit) and no hits found for this identifier
      level = 'No hit';
    } else {
      // Sources not tested or not verified all clear
      level = 'Not tested';
    }
  }

  return {
    level,
    categories,
    untested_sources,
    ...(suppressedCount > 0 ? { suppressed_count: suppressedCount } : {}),
  };
}
